/**
 * Voter-side primitives: ballot encryption, ballot verification.
 * See ADR 0008 § 4 and Belenios v2 specification § 4.
 *
 * `encryptBallot` runs on the voter's device only — it sees the
 * trustees' combined public key but never any private trustee share.
 * The output is a self-contained `EncryptedBallot` carrying the
 * indicator ciphertexts, per-option 0/1 NIZK proofs, a sum-to-1 NIZK
 * proof, and a Schnorr signature with the per-ballot credential.
 *
 * `verifyBallot` is the cheap pre-flight every receiver runs (cast
 * endpoint, verifier CLI, audit app). It does not check eligibility —
 * the caller checks `ballot.credentialPublicKey` against the published
 * eligibility list separately.
 *
 * STATUS: M3 (encrypt). Benaloh challenge generators below remain
 * stubbed; the auditor app pulls them in M3.5.
 */
import { ristretto255 } from "@noble/curves/ed25519";

import type {
  BenalohChallenge,
  ChaumPedersenProof,
  DisjunctiveProof,
  ElGamalCiphertext,
  EncryptedBallot,
  RistrettoPoint,
  Scalar,
  VoterCredential,
} from "../types";
import {
  GROUP_ORDER,
  bytesToHex,
  concatBytes,
  encodePoint,
  encodeScalar,
  randomScalar,
  sha256,
} from "../internal/encoding";
import {
  chaumPedersenProve,
  chaumPedersenVerify,
  disjunctiveProve,
  disjunctiveVerify,
  schnorrSign,
  schnorrVerify,
} from "../internal/sigma";

const G = ristretto255.Point.BASE;
const ZERO = ristretto255.Point.ZERO;

// ---------------------------------------------------------------------------
// Encryption
// ---------------------------------------------------------------------------

export interface EncryptBallotInput {
  readonly ballotId: string;
  /** Ordered option IDs. Index = position in the indicator vector. */
  readonly options: ReadonlyArray<string>;
  /**
   * Number of seats the ballot fills. The voter MUST mark exactly
   * `seats` distinct options. For single-choice (preș, ref. da/nu)
   * pass 1; for multi-winner (CT 20/71, CN 10/25 etc.) pass k. The
   * sum-to-k NIZK binds this value via the proof context, so a
   * verifier with the wrong seats value will reject the ballot.
   */
  readonly seats: number;
  /**
   * The marked options. `chosen.length` MUST equal `seats`; entries
   * MUST be distinct and each must be in `options`. The order is
   * irrelevant — only the set of marked options matters for the
   * indicator vector and the resulting tally.
   */
  readonly chosen: ReadonlyArray<string>;
  /** Combined election public key from the verified DKG transcript. */
  readonly ballotPublicKey: RistrettoPoint;
  /** Voter's per-ballot credential. The signingKey never leaves this scope. */
  readonly credential: VoterCredential;
}

export function encryptBallot(input: EncryptBallotInput): EncryptedBallot {
  return encryptBallotWithRandomness(input).ballot;
}

export interface EncryptedBallotWithRandomness {
  readonly ballot: EncryptedBallot;
  /** Per-option ElGamal randomness, in option order. Required to build a
   * Benaloh challenge — never include with a cast ballot. */
  readonly randomnesses: ReadonlyArray<Scalar>;
}

/**
 * Encrypt a ballot and additionally return the per-option randomness.
 *
 * Use this when the caller may want to audit the ballot via a Benaloh
 * challenge. The randomness array MUST NOT be sent to the bulletin
 * server — sharing it allows anyone to decrypt the ballot. It is only
 * for two paths: (a) fed to `generateBenalohChallenge` to produce an
 * audit transcript before discarding, or (b) discarded immediately
 * after a regular cast.
 */
export function encryptBallotWithRandomness(
  input: EncryptBallotInput,
): EncryptedBallotWithRandomness {
  if (input.options.length < 2) {
    throw new Error("ballot must have at least two options");
  }
  if (!Number.isInteger(input.seats) || input.seats < 1) {
    throw new Error(`seats must be a positive integer, got ${input.seats}`);
  }
  if (input.seats > input.options.length) {
    throw new Error(
      `seats ${input.seats} cannot exceed option count ${input.options.length}`,
    );
  }
  const seen = new Set<string>();
  for (const id of input.options) {
    if (seen.has(id)) throw new Error(`duplicate option id ${id}`);
    seen.add(id);
  }
  if (input.chosen.length !== input.seats) {
    throw new Error(
      `chosen length ${input.chosen.length} must equal seats ${input.seats}`,
    );
  }
  const chosenSet = new Set<string>();
  for (const c of input.chosen) {
    if (chosenSet.has(c)) {
      throw new Error(`chosen contains duplicate option id ${c}`);
    }
    if (!seen.has(c)) {
      throw new Error(`chosen option ${c} is not in the options list`);
    }
    chosenSet.add(c);
  }
  const chosenIdxSet = new Set<number>();
  for (let i = 0; i < input.options.length; i++) {
    if (chosenSet.has(input.options[i]!)) chosenIdxSet.add(i);
  }

  const ciphertexts: ElGamalCiphertext[] = [];
  const randomnesses: Scalar[] = [];

  for (let i = 0; i < input.options.length; i++) {
    const r = randomScalar();
    const m = chosenIdxSet.has(i) ? G : ZERO;
    const c1 = G.multiply(r);
    const c2 = m.add(input.ballotPublicKey.multiply(r));
    ciphertexts.push({ c1, c2 });
    randomnesses.push(r);
  }

  const perOptionProofs: DisjunctiveProof[] = [];
  for (let i = 0; i < input.options.length; i++) {
    const branch: 0 | 1 = chosenIdxSet.has(i) ? 1 : 0;
    perOptionProofs.push(
      disjunctiveProve(
        branch,
        randomnesses[i]!,
        ciphertexts[i]!,
        input.ballotPublicKey,
        optionContext(input.ballotId, input.credential.publicKey, i),
      ),
    );
  }

  // Aggregate (C1, C2) = (sum c1_i, sum c2_i). With exactly k indicators
  // set to G, sum(m_i) = k*G, so C2 = k*G + h*sum(r_i) and
  // (C1, C2 - k*G) = (G^R, h^R) for R = sum(r_i). The sum proof is
  // DLEQ(G→C1, h→C2 - k*G) with witness R; the proof context includes
  // k so a verifier with the wrong seats value will reject.
  let C1: RistrettoPoint = ZERO;
  let C2: RistrettoPoint = ZERO;
  let R = 0n;
  for (let i = 0; i < ciphertexts.length; i++) {
    C1 = C1.add(ciphertexts[i]!.c1);
    C2 = C2.add(ciphertexts[i]!.c2);
    R = (R + randomnesses[i]!) % GROUP_ORDER;
  }
  const kG = G.multiply(BigInt(input.seats));
  const sumProof = chaumPedersenProve(
    R,
    G,
    C1,
    input.ballotPublicKey,
    C2.subtract(kG),
    sumContext(input.ballotId, input.credential.publicKey, input.seats),
  );

  const msgBytes = ballotCanonicalBytes(
    input.ballotId,
    input.credential.publicKey,
    ciphertexts,
    perOptionProofs,
    sumProof,
  );
  const signature = schnorrSign(
    input.credential.signingKey,
    input.credential.publicKey,
    msgBytes,
  );

  return {
    ballot: {
      ballotId: input.ballotId,
      credentialPublicKey: input.credential.publicKey,
      ciphertexts,
      perOptionProofs,
      sumProof,
      signature,
    },
    randomnesses,
  };
}

// ---------------------------------------------------------------------------
// Verification
// ---------------------------------------------------------------------------

export interface VerifyBallotInput {
  readonly ballot: EncryptedBallot;
  readonly ballotPublicKey: RistrettoPoint;
  /** Number of seats the ballot fills. Must match the value the voter
   * used at encryption time — bound into the sum-to-k proof context. */
  readonly seats: number;
}

export interface BallotVerifyResult {
  readonly valid: boolean;
  readonly errors: ReadonlyArray<string>;
}

/**
 * Pre-flight ballot verification: signature, per-option 0/1 proofs,
 * sum-to-1 proof. Eligibility (credentialPublicKey ∈ eligibility list)
 * is checked separately by the caller.
 */
export function verifyBallot(input: VerifyBallotInput): BallotVerifyResult {
  const { ballot, ballotPublicKey, seats } = input;
  const errors: string[] = [];

  if (!Number.isInteger(seats) || seats < 1) {
    errors.push(`seats must be a positive integer, got ${seats}`);
  }
  if (ballot.ciphertexts.length !== ballot.perOptionProofs.length) {
    errors.push(
      `ciphertexts length ${ballot.ciphertexts.length} != perOptionProofs length ${ballot.perOptionProofs.length}`,
    );
  }
  if (ballot.ciphertexts.length < 2) {
    errors.push("ballot must have at least two ciphertexts");
  }
  if (seats > ballot.ciphertexts.length) {
    errors.push(
      `seats ${seats} cannot exceed ciphertext count ${ballot.ciphertexts.length}`,
    );
  }

  // Signature first — cheap, prevents wasting cycles on a malformed payload.
  const msgBytes = ballotCanonicalBytes(
    ballot.ballotId,
    ballot.credentialPublicKey,
    ballot.ciphertexts,
    ballot.perOptionProofs,
    ballot.sumProof,
  );
  if (!schnorrVerify(ballot.credentialPublicKey, msgBytes, ballot.signature)) {
    errors.push("invalid Schnorr signature on ballot");
  }

  for (let i = 0; i < Math.min(ballot.ciphertexts.length, ballot.perOptionProofs.length); i++) {
    const ok = disjunctiveVerify(
      ballot.perOptionProofs[i]!,
      ballot.ciphertexts[i]!,
      ballotPublicKey,
      optionContext(ballot.ballotId, ballot.credentialPublicKey, i),
    );
    if (!ok) errors.push(`per-option proof ${i} failed`);
  }

  let C1: RistrettoPoint = ZERO;
  let C2: RistrettoPoint = ZERO;
  for (const ct of ballot.ciphertexts) {
    C1 = C1.add(ct.c1);
    C2 = C2.add(ct.c2);
  }
  const kG = G.multiply(BigInt(seats));
  const sumOk = chaumPedersenVerify(
    ballot.sumProof,
    G,
    C1,
    ballotPublicKey,
    C2.subtract(kG),
    sumContext(ballot.ballotId, ballot.credentialPublicKey, seats),
  );
  if (!sumOk) errors.push(`sum-to-${seats} proof failed`);

  return { valid: errors.length === 0, errors };
}

// ---------------------------------------------------------------------------
// Canonical byte streams (signature payload + proof contexts)
// ---------------------------------------------------------------------------

const TAG_OPTION = new TextEncoder().encode("OAR/voting/option/v1\0");
// Bumped to v2 with the addition of sum-to-k support — the seats value
// is bound into the proof context so a tampered seats parameter at
// verification time triggers a clean failure.
const TAG_SUM = new TextEncoder().encode("OAR/voting/sum/v2\0");
const TAG_BALLOT = new TextEncoder().encode("OAR/voting/ballot/v1\0");

function u32be(n: number): Uint8Array {
  const out = new Uint8Array(4);
  new DataView(out.buffer).setUint32(0, n, false);
  return out;
}

function utf8(s: string): Uint8Array {
  return new TextEncoder().encode(s);
}

function lp(bytes: Uint8Array): Uint8Array {
  return concatBytes(u32be(bytes.length), bytes);
}

function optionContext(
  ballotId: string,
  pk: RistrettoPoint,
  optionIndex: number,
): Uint8Array {
  return concatBytes(
    TAG_OPTION,
    lp(utf8(ballotId)),
    encodePoint(pk),
    u32be(optionIndex),
  );
}

function sumContext(
  ballotId: string,
  pk: RistrettoPoint,
  seats: number,
): Uint8Array {
  return concatBytes(TAG_SUM, lp(utf8(ballotId)), encodePoint(pk), u32be(seats));
}

function ciphertextBytes(c: ElGamalCiphertext): Uint8Array {
  return concatBytes(encodePoint(c.c1), encodePoint(c.c2));
}

function disjunctiveBytes(p: DisjunctiveProof): Uint8Array {
  return concatBytes(
    encodePoint(p.t1_0),
    encodePoint(p.t2_0),
    encodePoint(p.t1_1),
    encodePoint(p.t2_1),
    encodeScalar(((p.e0 % GROUP_ORDER) + GROUP_ORDER) % GROUP_ORDER),
    encodeScalar(((p.s0 % GROUP_ORDER) + GROUP_ORDER) % GROUP_ORDER),
    encodeScalar(((p.e1 % GROUP_ORDER) + GROUP_ORDER) % GROUP_ORDER),
    encodeScalar(((p.s1 % GROUP_ORDER) + GROUP_ORDER) % GROUP_ORDER),
  );
}

function chaumPedersenBytes(p: ChaumPedersenProof): Uint8Array {
  return concatBytes(
    encodePoint(p.t1),
    encodePoint(p.t2),
    encodeScalar(((p.s % GROUP_ORDER) + GROUP_ORDER) % GROUP_ORDER),
  );
}

/**
 * Canonical bytes the voter signs and the verifier reproduces.
 * Domain-separated by `TAG_BALLOT`; field order pinned here.
 */
function ballotCanonicalBytes(
  ballotId: string,
  credentialPublicKey: RistrettoPoint,
  ciphertexts: ReadonlyArray<ElGamalCiphertext>,
  perOptionProofs: ReadonlyArray<DisjunctiveProof>,
  sumProof: ChaumPedersenProof,
): Uint8Array {
  const ctBytes = ciphertexts.map(ciphertextBytes);
  const proofBytes = perOptionProofs.map(disjunctiveBytes);
  return concatBytes(
    TAG_BALLOT,
    lp(utf8(ballotId)),
    encodePoint(credentialPublicKey),
    u32be(ciphertexts.length),
    ...ctBytes,
    u32be(perOptionProofs.length),
    ...proofBytes,
    chaumPedersenBytes(sumProof),
  );
}

// ---------------------------------------------------------------------------
// Receipts
// ---------------------------------------------------------------------------

/**
 * Voter receipt = SHA-256 of the canonical signed bytes, hex-encoded.
 * Stable across server reloads; voter cross-checks the first 8 chars
 * shown in the UI against the bulletin board entry.
 */
export function ballotReceiptHash(ballot: EncryptedBallot): string {
  const bytes = ballotCanonicalBytes(
    ballot.ballotId,
    ballot.credentialPublicKey,
    ballot.ciphertexts,
    ballot.perOptionProofs,
    ballot.sumProof,
  );
  return bytesToHex(sha256(bytes));
}

// ---------------------------------------------------------------------------
// Benaloh challenge (audit-cast)
// ---------------------------------------------------------------------------

export interface GenerateBenalohChallengeInput {
  readonly ballot: EncryptedBallot;
  readonly options: ReadonlyArray<string>;
  readonly seats: number;
  readonly chosen: ReadonlyArray<string>;
  readonly randomnesses: ReadonlyArray<Scalar>;
}

/**
 * Bundle the audit material from an encrypted-but-not-cast ballot. The
 * returned challenge contains everything an external verifier needs to
 * recompute the indicator ciphertexts and check the client encrypted
 * the options the voter actually chose; the voter must NOT cast this
 * ballot afterwards because the revealed randomness leaks the
 * plaintext to anyone holding the ballot public key.
 */
export function generateBenalohChallenge(
  input: GenerateBenalohChallengeInput,
): BenalohChallenge {
  if (input.options.length !== input.randomnesses.length) {
    throw new Error(
      `options length ${input.options.length} != randomnesses length ${input.randomnesses.length}`,
    );
  }
  if (input.options.length !== input.ballot.ciphertexts.length) {
    throw new Error(
      `options length ${input.options.length} != ballot.ciphertexts length ${input.ballot.ciphertexts.length}`,
    );
  }
  if (!Number.isInteger(input.seats) || input.seats < 1) {
    throw new Error(`seats must be a positive integer, got ${input.seats}`);
  }
  if (input.chosen.length !== input.seats) {
    throw new Error(
      `chosen length ${input.chosen.length} != seats ${input.seats}`,
    );
  }
  const chosenSet = new Set<string>();
  for (const c of input.chosen) {
    if (chosenSet.has(c)) throw new Error(`chosen contains duplicate ${c}`);
    if (input.options.indexOf(c) < 0) throw new Error(`chosen ${c} not in options`);
    chosenSet.add(c);
  }
  return {
    ballotId: input.ballot.ballotId,
    credentialPublicKey: input.ballot.credentialPublicKey,
    options: [...input.options],
    seats: input.seats,
    chosen: [...input.chosen],
    randomnesses: [...input.randomnesses],
    ciphertexts: input.ballot.ciphertexts.map((c) => ({ c1: c.c1, c2: c.c2 })),
  };
}

export interface VerifyBenalohChallengeInput {
  readonly challenge: BenalohChallenge;
  readonly ballotPublicKey: RistrettoPoint;
}

export interface BenalohVerifyResult {
  readonly valid: boolean;
  readonly errors: ReadonlyArray<string>;
}

/**
 * Re-encrypt the indicator vector implied by `chosen` with the revealed
 * randomness and verify each ciphertext matches the field carried in
 * the challenge. The aggregate plaintext equals exactly G (sum-to-1)
 * by construction; the per-option NIZK proofs from the original
 * encrypted ballot are NOT re-checked here — that is the role of
 * `verifyBallot` on the cast bulletin entry, while the Benaloh check
 * is specifically about cast-as-intended.
 */
export function verifyBenalohChallenge(
  input: VerifyBenalohChallengeInput,
): BenalohVerifyResult {
  const errors: string[] = [];
  const { challenge, ballotPublicKey } = input;

  if (challenge.chosen.length !== challenge.seats) {
    errors.push(
      `chosen length ${challenge.chosen.length} != seats ${challenge.seats}`,
    );
  }
  const chosenSet = new Set<string>();
  for (const c of challenge.chosen) {
    if (chosenSet.has(c)) errors.push(`chosen contains duplicate ${c}`);
    if (challenge.options.indexOf(c) < 0) {
      errors.push(`chosen ${c} not in options`);
    }
    chosenSet.add(c);
  }
  if (challenge.options.length !== challenge.randomnesses.length) {
    errors.push(
      `options length ${challenge.options.length} != randomnesses length ${challenge.randomnesses.length}`,
    );
  }
  if (challenge.options.length !== challenge.ciphertexts.length) {
    errors.push(
      `options length ${challenge.options.length} != ciphertexts length ${challenge.ciphertexts.length}`,
    );
  }
  if (errors.length > 0) return { valid: false, errors };

  for (let i = 0; i < challenge.options.length; i++) {
    const r = ((challenge.randomnesses[i]! % GROUP_ORDER) + GROUP_ORDER) % GROUP_ORDER;
    const m = chosenSet.has(challenge.options[i]!) ? G : ZERO;
    const c1Expected = G.multiply(r);
    const c2Expected = m.add(ballotPublicKey.multiply(r));
    const ct = challenge.ciphertexts[i]!;
    if (!c1Expected.equals(ct.c1)) {
      errors.push(`ciphertext ${i}: c1 mismatch`);
    }
    if (!c2Expected.equals(ct.c2)) {
      errors.push(`ciphertext ${i}: c2 mismatch`);
    }
  }

  return { valid: errors.length === 0, errors };
}
